import { createContext, useCallback, useContext } from 'react'

import { useEnabledModules } from '../hooks/useEnabledModules'
import { useHasRoles } from '../hooks/useHasRoles'
import { Role } from '../modules/adminUsers/roles'

import { useUser } from './userContext'

import { AccessProfileRole, BuildPassModule } from 'types/api.graphql'

type AccessProfileContextValue = {
  validateCanEditAccessProfile: (
    accessProfile: { owner?: { id: string } } | undefined | null
  ) => boolean
  validateCanMutateEntity: (
    accessProfile:
      | {
          id: string
          defaultRole?: AccessProfileRole
          owner?: { id: string }
          actors?: { adminUser: { id: string }; role: AccessProfileRole }[]
        }
      | undefined
      | null
  ) => boolean
  canManageAccessTemplates: boolean
  accessProfilesEnabled: boolean
}

const ACCESS_PROFILE_OVERRIDE_ROLES: Role[] = [
  Role.SuperAdmin,
  Role.BpSuperAdmin,
  Role.BuilderOwner,
]

const AccessProfileContext = createContext<AccessProfileContextValue>({
  validateCanEditAccessProfile: () => true,
  validateCanMutateEntity: () => true,
  canManageAccessTemplates: false,
  accessProfilesEnabled: false,
})

export const AccessProfileProvider = ({ children }) => {
  const { adminUser } = useUser()
  const canOverrideAccessProfile = useHasRoles(ACCESS_PROFILE_OVERRIDE_ROLES)
  const { isModuleEnabled } = useEnabledModules()
  const accessProfilesEnabled = isModuleEnabled(BuildPassModule.AccessProfiles)

  const canManageAccessTemplates = useHasRoles([
    Role.BuilderManager,
    Role.BuilderOwner,
    Role.SuperAdmin,
  ])

  const isProjectManager = useHasRoles([Role.BuilderProjectManager])

  const validateCanEditAccessProfile = useCallback(
    (accessProfile: { owner?: { id: string } } | undefined | null) => {
      if (!accessProfilesEnabled) {
        return false
      }

      if (canOverrideAccessProfile) {
        return true
      }

      const isOwner = accessProfile?.owner?.id === adminUser.id

      if (isProjectManager) {
        return isOwner
      }

      return !accessProfile?.owner || isOwner
    },
    [
      adminUser?.id,
      accessProfilesEnabled,
      canOverrideAccessProfile,
      isProjectManager,
    ]
  )

  const validateCanMutateEntity = useCallback(
    (accessProfile: {
      id: string
      defaultRole: AccessProfileRole
      owner: { id: string }
      actors: { adminUser: { id: string }; role: AccessProfileRole }[]
    }) => {
      if (!accessProfile || !accessProfilesEnabled || canOverrideAccessProfile)
        return true

      const { defaultRole, actors, owner } = accessProfile

      if (
        defaultRole === AccessProfileRole.ReadWrite ||
        owner.id === adminUser.id
      )
        return true

      const matchingActor = actors?.find(
        (actor) => actor.adminUser.id === adminUser.id
      )

      if (!matchingActor) return false

      return matchingActor.role === AccessProfileRole.ReadWrite
    },
    [adminUser, accessProfilesEnabled, canOverrideAccessProfile]
  )

  return (
    <AccessProfileContext.Provider
      value={{
        validateCanEditAccessProfile,
        validateCanMutateEntity,
        accessProfilesEnabled: accessProfilesEnabled,
        canManageAccessTemplates,
      }}
    >
      {children}
    </AccessProfileContext.Provider>
  )
}

export const useAccessProfile = () => useContext(AccessProfileContext)
